import { useState, useEffect, useRef } from 'react';
import { Circle, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import type { RadiusCircle } from './types';
import { haversineMeters, destinationPoint } from './geo';

/**
 * An interactive radius circle: drag the center dot to move it, drag the edge dot
 * to resize it. The circle + mileage label update live; `onCommit` fires on release
 * so the parent can re-pull results without hammering the API mid-drag.
 */

const METERS_PER_MILE = 1609.34;
const clampMiles = (m: number) => Math.max(0.1, Math.min(50, m));

const centerIcon = L.divIcon({
  className: '',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  html: `<div style="width:14px;height:14px;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);cursor:move;"></div>`,
});
const edgeIcon = L.divIcon({
  className: '',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  html: `<div style="width:13px;height:13px;border-radius:50%;background:#fff;border:2.5px solid #2563eb;box-shadow:0 1px 3px rgba(0,0,0,.4);cursor:ew-resize;"></div>`,
});

interface Props {
  circle: RadiusCircle;
  onResize: (id: number, miles: number) => void; // live during drag, no fetch
  onMove: (id: number, lat: number, lng: number) => void; // live during drag, no fetch
  onCommit: (id: number) => void; // on release -> re-fetch results
}

export default function RadiusCircleLayer({ circle, onResize, onMove, onCommit }: Props) {
  // Edge handle sits due-east of the center at the current radius, except while it is
  // being dragged (then it follows the cursor and drives the radius).
  const [edgePos, setEdgePos] = useState<[number, number]>(() =>
    destinationPoint(circle.lat, circle.lng, circle.miles * METERS_PER_MILE, 90),
  );
  const draggingEdge = useRef(false);

  useEffect(() => {
    if (!draggingEdge.current) {
      setEdgePos(destinationPoint(circle.lat, circle.lng, circle.miles * METERS_PER_MILE, 90));
    }
  }, [circle.lat, circle.lng, circle.miles]);

  return (
    <>
      <Circle
        center={[circle.lat, circle.lng]}
        radius={circle.miles * METERS_PER_MILE}
        pathOptions={{ color: '#2563eb', weight: 2, fillColor: '#2563eb', fillOpacity: 0.08 }}
      />

      {/* Center handle — drag to move the whole circle */}
      <Marker
        position={[circle.lat, circle.lng]}
        icon={centerIcon}
        draggable
        zIndexOffset={1000}
        eventHandlers={{
          drag: (e) => {
            const ll = (e.target as L.Marker).getLatLng();
            onMove(circle.id, ll.lat, ll.lng);
          },
          dragend: () => onCommit(circle.id),
        }}
      />

      {/* Edge handle — drag to resize */}
      <Marker
        position={edgePos}
        icon={edgeIcon}
        draggable
        zIndexOffset={1000}
        eventHandlers={{
          dragstart: () => { draggingEdge.current = true; },
          drag: (e) => {
            const ll = (e.target as L.Marker).getLatLng();
            setEdgePos([ll.lat, ll.lng]);
            const miles = clampMiles(haversineMeters([circle.lat, circle.lng], [ll.lat, ll.lng]) / METERS_PER_MILE);
            onResize(circle.id, miles);
          },
          dragend: () => {
            draggingEdge.current = false;
            setEdgePos(destinationPoint(circle.lat, circle.lng, circle.miles * METERS_PER_MILE, 90));
            onCommit(circle.id);
          },
        }}
      >
        <Tooltip permanent direction="top" offset={[0, -6]} className="radius-mile-tooltip">
          {circle.miles.toFixed(1)} mi
        </Tooltip>
      </Marker>
    </>
  );
}
